import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    OnInit,
    untracked,
} from '@angular/core';
import { form, FormField, readonly } from '@angular/forms/signals';
import {
    clearPrivacyPerimeter,
    LocationTrackerUserConfigState,
    openLocationTrackerPage,
    requestPrivacyPerimeter,
    saveLocationTrackerUserConfig,
    selectIsLocationTrackerDraftReady,
    selectIsLocationTrackerEnabled,
    selectIsLocationTrackerSaving,
    selectIsPrivacyPerimeterRequestPending,
    selectIsPrivacyPerimeterSet,
    selectLocationTrackerAdminConfig,
    selectLocationTrackerSaveMessage,
    selectLocationTrackerUserConfig,
    selectPrivacyPerimeterErrorKey,
    updateLocationTrackerUserConfig,
} from '@evorait/pace-core';
import {
    CheckboxFieldComponent,
    PacePrivacyPerimeterPickerComponent,
    PaceSaveActionComponent,
    PaceSaveActionMessage,
    TextFieldComponent,
} from '@evorait/pace-components';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import isEqual from 'lodash/isEqual';

/**
 * Mutable counterpart of {@link LocationTrackerUserConfigState}: signal-forms paths need a
 * writable target, and the slice's own state type is `Readonly<>`.
 *
 * Carries the privacy-perimeter coordinates even though no field on this page binds to them
 * directly — see {@link LocationTrackerComponent.model}'s doc comment for why dropping them
 * would be a bug, not a simplification.
 */
type LocationTrackerFormModel = {
    shareLocation: boolean;
    state: string;
    businessLine: string;
    privacyPerimeterLatitude: number | undefined;
    privacyPerimeterLongitude: number | undefined;
};

/**
 * The location-tracker settings page.
 *
 * Owns exactly three things: seeding the draft on entry (replacing the resolver that used to
 * block navigation for it), binding one form to the store's projection of that draft and writing
 * every edit straight back as an action, and reading the two admin-configured, read-only values
 * from the app-config store slice like everything else on this page. Everything else — seeding
 * the draft from the user store, saving it back, the geolocation lookup and its error mapping,
 * and the timed save banner — lives in `LocationTrackerEffects` and the reducer; this component
 * only dispatches and renders.
 */
@Component({
    selector: 'pace-location-tracker',
    standalone: true,
    imports: [
        FormField,
        TextFieldComponent,
        CheckboxFieldComponent,
        PacePrivacyPerimeterPickerComponent,
        PaceSaveActionComponent,
        TranslateModule,
    ],
    templateUrl: './location-tracker.component.html',
    styleUrl: './location-tracker.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationTrackerComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);

    /**
     * Whether the draft has been seeded from the user store.
     *
     * Gated on this — not on the user store merely being "loaded", which a cache rehydration also
     * satisfies — for the same reason as before the migration: both a cache read and a network
     * request start when the user store loads, and the cache normally wins, so rendering as soon
     * as *something* arrived would open the form on cached values and then swap them out when the
     * network reply landed, under the fingers of anyone who had started typing. The seed effect
     * (`LocationTrackerEffects.seedLocationTrackerDraft$`) only seeds `draft` once the user store
     * has settled, so gating on the draft's presence keeps the same guarantee the route resolver
     * — and later `hasBackendSettled` — used to give; only the mechanism moved.
     */
    protected readonly isDraftReady = this.store.selectSignal(selectIsLocationTrackerDraftReady);

    protected readonly isPrivacyPerimeterSet = this.store.selectSignal(selectIsPrivacyPerimeterSet);
    protected readonly isPrivacyPerimeterRequestPending = this.store.selectSignal(
        selectIsPrivacyPerimeterRequestPending,
    );
    private readonly perimeterErrorKey = this.store.selectSignal(selectPrivacyPerimeterErrorKey);

    protected readonly isSaving = this.store.selectSignal(selectIsLocationTrackerSaving);
    private readonly saveMessageState = this.store.selectSignal(selectLocationTrackerSaveMessage);

    /** Admin-configured radius/precision, and whether the feature is on at all. */
    protected readonly locationTrackerConfig = this.store.selectSignal(selectLocationTrackerAdminConfig);
    protected readonly isLocationTrackerEnabled = this.store.selectSignal(selectIsLocationTrackerEnabled);

    /** The (possibly still-being-edited) config, straight from the store. */
    private readonly storedConfig = this.store.selectSignal(selectLocationTrackerUserConfig);

    /**
     * What the form binds to.
     *
     * A `linkedSignal` rather than a plain one for the same reason as the timesheet's `model`: the
     * store is the truth, this follows it, and {@link writeThrough} sends every edit straight back
     * out. There is no separate draft to keep in step and no commit step to remember.
     *
     * The perimeter coordinates are copied in even though no field here edits them. `writeThrough`
     * below sends the *whole* model back on every change, so if the coordinates were left out of
     * it, typing a single character into the State field would round-trip a config with the
     * perimeter silently stripped out — the page would erase a perimeter it never touched.
     */
    protected readonly model = linkedSignal<LocationTrackerUserConfigState, LocationTrackerFormModel>({
        source: this.storedConfig,
        computation: (fromStore) => ({ ...fromStore }),
    });

    /**
     * Sends any edit the form made back to the store. Compares against the store rather than
     * tracking dirtiness, exactly as the timesheet's write-through does: whatever the store ends up
     * holding — including a value it normalized — is what the model is reconciled to, so the round
     * trip settles once the two agree and nothing loops.
     *
     * No `wouldWorsenOrder`-style guard is needed here: there is no ordering constraint between
     * this page's fields for an edit to violate.
     */
    private readonly writeThrough = effect(() => {
        const edited = this.model();
        const stored = untracked(this.storedConfig);
        if (!isEqual(edited, stored)) {
            this.store.dispatch(updateLocationTrackerUserConfig({ config: edited }));
        }
    });

    /**
     * One rule, faithfully porting the legacy `[isReadonlyInputControl]="(isLocationTrackerEnabled$
     * | async) === false"` on the share-location checkbox. No other validation is added: the legacy
     * page had none, and the State/BusinessLine fields stayed editable even with the feature off —
     * that looks inconsistent but is existing behaviour, not an oversight, so it is left alone.
     */
    protected readonly locationTrackerForm = form(this.model, (path) => {
        readonly(path.shareLocation, () => !this.isLocationTrackerEnabled());
    });

    /**
     * Already-translated error text for the perimeter picker, or `''` when there is nothing to show.
     *
     * `instant` rather than a reactive translation pipe: this text is driven entirely by the error
     * key changing (cleared on the next lookup), not by the active language, so it does not need to
     * re-run on a language switch.
     */
    protected readonly perimeterErrorText = computed(() => {
        const key = this.perimeterErrorKey();
        return key ? <string>this.translate.instant(key) : '';
    });

    /**
     * Already-translated save banner for `pace-save-action`, or `undefined` while there is none.
     *
     * `instant` again: the banner is shown for a fixed window (`SAVE_MESSAGE_VISIBLE_MS`) and then
     * cleared by the effect, so a language switch mid-display is an acceptable rough edge rather
     * than something worth a reactive wrapper.
     */
    protected readonly saveMessage = computed<PaceSaveActionMessage | undefined>(() => {
        const message = this.saveMessageState();
        return message ? { text: <string>this.translate.instant(message.key), isError: message.isError } : undefined;
    });

    /** Replaces the route resolver: the draft is seeded from the user store once the page itself is up. */
    public ngOnInit(): void {
        this.store.dispatch(openLocationTrackerPage());
    }

    protected selectLocation(): void {
        this.store.dispatch(requestPrivacyPerimeter());
    }

    protected clearLocation(): void {
        this.store.dispatch(clearPrivacyPerimeter());
    }

    protected save(): void {
        this.store.dispatch(saveLocationTrackerUserConfig());
    }
}
